import { debug } from './debug';

/**
 * Check if a pricing rule is valid for a given reference date.
 *
 * Validates against:
 * - valid_from: rule not yet active (date comparison)
 * - valid_to: rule expired (date comparison)
 * - valid_days_of_week: day-of-week restriction, ISO-8601 numbering
 *   (1=Monday ... 7=Sunday). See the note in the check below — this is NOT
 *   JS getDay() numbering.
 *
 * Rules with NULL/undefined/empty values for these fields are treated as "applies always".
 *
 * @param {Object} rule - A pricing rule object from the database
 * @param {Date} referenceDate - The rental start date to validate against
 * @returns {boolean} Whether the rule is valid for the given date
 */
export function isPricingRuleValid(rule, referenceDate) {
  if (!referenceDate) return true;

  const refDate = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);

  // Extract date-only portion (YYYY-MM-DD) for valid_from/valid_to comparisons.
  // These DB fields are DATE type (no time component), so we compare dates only
  // to avoid timezone/time-of-day mismatches.
  const refDateOnly = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());

  // valid_from: rule not yet active
  if (rule.valid_from) {
    const validFrom = new Date(rule.valid_from + 'T00:00:00');
    const fromDateOnly = new Date(validFrom.getFullYear(), validFrom.getMonth(), validFrom.getDate());
    if (refDateOnly < fromDateOnly) return false;
  }

  // valid_to: rule expired
  if (rule.valid_to) {
    const validTo = new Date(rule.valid_to + 'T00:00:00');
    const toDateOnly = new Date(validTo.getFullYear(), validTo.getMonth(), validTo.getDate());
    if (refDateOnly > toDateOnly) return false;
  }

  // valid_days_of_week is stored in ISO-8601 numbering: 1=Monday ... 7=Sunday.
  //
  // It used to be JS getDay() numbering (0=Sunday ... 6=Saturday), and the two
  // agree for Monday-Saturday — they differ ONLY on Sunday (0 vs 7). Migration
  // 137 (owned by clickandshare-frontend-v3, applied to the shared database)
  // rewrote 0 -> 7 and added a CHECK constraint pinning values to 1-7, so this
  // reader silently stopped matching Sunday-restricted rules the moment it ran:
  // `[7].includes(0)` is false, so the rule was dropped and the add-on lost its
  // price on Sundays. Nothing else broke, which is why it went unnoticed.
  //
  // The legacy 0 is still accepted as a fallback. It can only ever match a
  // Sunday, so it cannot produce a false positive on another weekday, and it
  // keeps this working against any environment whose database predates 137.
  //
  // null or empty array means applies to all days.
  if (rule.valid_days_of_week && rule.valid_days_of_week.length > 0) {
    const jsDay = refDate.getDay();          // 0=Sunday ... 6=Saturday
    const isoDay = jsDay === 0 ? 7 : jsDay;  // 1=Monday ... 7=Sunday
    const matches =
      rule.valid_days_of_week.includes(isoDay) ||
      rule.valid_days_of_week.includes(jsDay);
    if (!matches) return false;
  }

  return true;
}

/**
 * Filter an array of pricing rules to only include those valid for a given date and flow type.
 * Combines the existing active check with the new validity checks.
 *
 * @param {Array} pricingRules - Array of pricing rule objects
 * @param {Date} referenceDate - The rental start date to validate against
 * @param {Object} [options] - Optional filtering options
 * @param {string} [options.flowType] - Booking flow type: 'immediate' or 'reservation'. If omitted, no flow filtering is applied.
 * @returns {Array} Filtered pricing rules that are active and valid
 */
export function getValidPricingRules(pricingRules, referenceDate, options = {}) {
  if (!pricingRules || pricingRules.length === 0) return [];

  const { flowType } = options;

  const result = pricingRules.filter(pr => {
    const isActive = pr.active !== false;
    const isValid = isPricingRuleValid(pr, referenceDate);
    // Safety net: exclude rules not available on the 'online' channel
    const isOnlineChannel = !pr.channels || pr.channels.includes('online');
    // Flow type filter: NULL/undefined flow_type on the rule means "both flows"
    const isFlowMatch = !pr.flow_type || !flowType || pr.flow_type === flowType;
    if (!isActive || !isValid || !isOnlineChannel || !isFlowMatch) {
      debug.log(`🚫 [getValidPricingRules] Filtered out rule ${pr.id}: active=${isActive}, valid=${isValid}, onlineChannel=${isOnlineChannel}, flowMatch=${isFlowMatch}`, {
        price: pr.price, duration_type: pr.duration_type, duration_value: pr.duration_value,
        valid_from: pr.valid_from, valid_to: pr.valid_to, valid_days_of_week: pr.valid_days_of_week,
        channels: pr.channels, flow_type: pr.flow_type, requestedFlowType: flowType,
        referenceDate: referenceDate?.toString?.() || referenceDate
      });
    }
    return isActive && isValid && isOnlineChannel && isFlowMatch;
  });

  debug.log(`✅ [getValidPricingRules] ${result.length}/${pricingRules.length} rules passed validation (flowType=${flowType || 'any'})`);
  return result;
}

/**
 * Match a duration ID (e.g., "4h", "1d", "1w") to the best pricing rule.
 *
 * @param {Array} pricingRules - Array of pricing rule objects
 * @param {string} durationId - Duration identifier (e.g., "4h", "1d", "1w")
 * @returns {Object|null} The matching pricing rule, or null if no rules available
 */
export function findMatchingPricingRule(pricingRules, durationId) {
  if (!pricingRules || pricingRules.length === 0) {
    return null;
  }

  // Parse duration ID (e.g., "4h", "1d", "1w")
  const match = durationId?.match(/^(\d+)([hdw])$/);
  if (!match) {
    return pricingRules.find(p => p.featured) || pricingRules[0];
  }

  const [, value, unit] = match;
  const durationValue = parseInt(value);

  // Try exact match first, then fall back to next largest rule
  if (unit === 'h') {
    const matchingRule = pricingRules.find(r =>
      r.duration_type === 'hours' && parseFloat(r.duration_value) === durationValue
    );
    if (matchingRule) return matchingRule;

    const hourRules = pricingRules
      .filter(r => r.duration_type === 'hours')
      .map(r => ({ rule: r, hours: parseFloat(r.duration_value) }))
      .sort((a, b) => a.hours - b.hours);
    const nextLargestHour = hourRules.find(entry => entry.hours >= durationValue);
    if (nextLargestHour) return nextLargestHour.rule;
    if (hourRules.length > 0) return hourRules[hourRules.length - 1].rule;
  } else if (unit === 'd') {
    const matchingRule = pricingRules.find(r =>
      (r.duration_type === 'calendar_day' || r.duration_type === 'multi_day') &&
      parseFloat(r.duration_value) === durationValue
    );
    if (matchingRule) return matchingRule;
  } else if (unit === 'w') {
    const daysInWeeks = durationValue * 7;
    const matchingRule = pricingRules.find(r => {
      if (r.duration_type === 'week') {
        return parseFloat(r.duration_value) === durationValue;
      }
      if (r.duration_type === 'multi_day') {
        return parseFloat(r.duration_value) === daysInWeeks;
      }
      return false;
    });
    if (matchingRule) return matchingRule;
  }

  // If no exact match, find the next largest pricing rule across all types
  // by normalizing to hours for comparison
  const targetHours = unit === 'h' ? durationValue : unit === 'd' ? durationValue * 24 : durationValue * 7 * 24;
  const allRulesNormalized = pricingRules
    .map(rule => {
      let hours;
      if (rule.duration_type === 'hours') hours = parseFloat(rule.duration_value);
      else if (rule.duration_type === 'calendar_day' || rule.duration_type === 'multi_day') hours = parseFloat(rule.duration_value) * 24;
      else if (rule.duration_type === 'week') hours = parseFloat(rule.duration_value) * 7 * 24;
      else hours = parseFloat(rule.duration_value);
      return { rule, hours };
    })
    .sort((a, b) => a.hours - b.hours);

  const nextLargest = allRulesNormalized.find(entry => entry.hours >= targetHours);
  if (nextLargest) return nextLargest.rule;
  if (allRulesNormalized.length > 0) return allRulesNormalized[allRulesNormalized.length - 1].rule;

  // Fallback to featured or first rule
  return pricingRules.find(p => p.featured) || pricingRules[0];
}
